import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useAudioAlarm — plays a programmatic siren using the Web Audio API.
 * No external audio file needed. Uses oscillators to generate an urgent siren tone.
 * The siren loops until stopped.
 */
export function useAudioAlarm() {
  const [isPlaying, setIsPlaying] = useState(false);
  const ctxRef = useRef(null);
  const intervalRef = useRef(null);
  const isPlayingRef = useRef(false);

  const playSiren = useCallback(() => {
    try {
      // Defensive: clear any existing loop/context before starting a new one.
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (ctxRef.current) {
        ctxRef.current.close().catch(() => {});
        ctxRef.current = null;
      }

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;

      // Resume context if suspended (browser autoplay policy)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      const playBurst = () => {
        if (!ctxRef.current || ctxRef.current.state === 'closed') return;
        const now = ctxRef.current.currentTime;
        for (let i = 0; i < 4; i++) {
          const osc = ctxRef.current.createOscillator();
          const gain = ctxRef.current.createGain();
          osc.connect(gain);
          gain.connect(ctxRef.current.destination);
          osc.type = 'sawtooth';
          const t = now + i * 0.5;
          osc.frequency.setValueAtTime(800, t);
          osc.frequency.linearRampToValueAtTime(1200, t + 0.25);
          osc.frequency.linearRampToValueAtTime(800, t + 0.45);
          gain.gain.setValueAtTime(0.04, t);
          gain.gain.setValueAtTime(0.04, t + 0.4);
          gain.gain.linearRampToValueAtTime(0, t + 0.48);
          osc.start(t);
          osc.stop(t + 0.5);
        }
      };

      // Play initial burst + loop
      playBurst();
      intervalRef.current = setInterval(playBurst, 2000);
    } catch (e) {
      console.warn('Audio alarm failed:', e);
    }
  }, []);

  const playAlarm = useCallback(() => {
    if (isPlayingRef.current || intervalRef.current || ctxRef.current) return;
    isPlayingRef.current = true;
    playSiren();
    setIsPlaying(true);
  }, [playSiren]);

  const stopAlarm = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (ctxRef.current) {
      ctxRef.current.close().catch(() => {});
      ctxRef.current = null;
    }
    isPlayingRef.current = false;
    setIsPlaying(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (ctxRef.current) ctxRef.current.close().catch(() => {});
    };
  }, []);

  return { playAlarm, stopAlarm, isPlaying };
}
